import { useIntervalEffect } from '@helloself/hello-self-ui';
import { isBefore } from 'date-fns';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getAllConversationStatusesAfterTimestamp, getConversationStatus } from '../api/messaging/messaging';
import { ConversationStatus } from '../api/messaging/types';
import { useAppDispatch } from '../app/hooks';
import {
    populateConversationMessagesSinceLastFetch,
    populateInitialConversationMessages,
} from '../features/messaging/model/asyncReducers/messaging';
import {
    selectActiveExpertConversation,
    selectExpertConversations,
    selectHasInitialisedMessages,
    selectHaveUnreadMessages,
} from '../features/messaging/model/selectors';
import {
    updateActiveExpertConversationId,
    updateConversationStatus,
    updateConversationStatuses,
} from '../features/messaging/model/slice';
import { selectMyConnectionsFeatureFlag } from '../features/user/selectors';

export const usePollForMessages = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const activeConversationId = useSelector(selectActiveExpertConversation);

    const [sixtySecondTicker, setSixtySecondTicker] = useState(0);

    const [lastSyncTimestamp, setLastSyncTimestamp] = useState<Date | undefined>(undefined);
    const [lastAllConvosSyncTimestamp, setLastAllConvosSyncTimestamp] = useState<string | undefined>(undefined);

    const allE2EConversations = useSelector(selectExpertConversations);
    const haveUnreadMessages = useSelector(selectHaveUnreadMessages);
    const haveInitialisedConversations = useSelector(selectHasInitialisedMessages);
    // TODO : remove when ready
    const isConnections = useSelector(selectMyConnectionsFeatureFlag);

    const loadInitialMessagesForConversation = async (conversationId: string, latestMessageSeqId: number) => {
        dispatch(
            populateInitialConversationMessages({
                conversationId: conversationId,
                latestMessageSeqId: latestMessageSeqId,
            })
        );
    };

    const checkForNewActiveConversationMessages = async () => {
        if (!activeConversationId) {
            return;
        }

        const status = await getConversationStatus({
            conversationId: activeConversationId,
        });

        if (!status) {
            return;
        }

        if (lastSyncTimestamp && isBefore(lastSyncTimestamp, new Date(status.latestMessageTimestamp))) {
            dispatch(
                populateConversationMessagesSinceLastFetch({
                    conversationId: activeConversationId,
                    seqId: status.latestReadSeqId,
                })
            );
        } else if (lastSyncTimestamp === undefined && location.pathname.includes(`/messaging`)) {
            if (!haveInitialisedConversations.includes(activeConversationId)) {
                await loadInitialMessagesForConversation(activeConversationId, status.latestMessageSeqId);
            }
        }

        dispatch(updateConversationStatus({ status: status }));
        dispatch(updateActiveExpertConversationId({ conversationId: activeConversationId }));

        setLastSyncTimestamp(new Date());
    };

    const checkForAllConversationMessages = async () => {
        try {
            const response = await getAllConversationStatusesAfterTimestamp({
                timestamp: lastAllConvosSyncTimestamp || allE2EConversations.forEventsUntilTimestamp,
            });

            if (!response) {
                console.error(`useMonitorE2EMessages: getAllE2EConversationStatusesAfterTimestamp returned undefined`);
                return;
            }

            if (response.conversationStatuses) {
                dispatch(updateConversationStatuses({ statuses: response }));
                setLastAllConvosSyncTimestamp(response.forEventsUntilTimestamp);
            }

            if (activeConversationId) {
                const nonActiveConversationStatuses = {
                    forEventsAfterTimestamp: response?.forEventsAfterTimestamp,
                    forEventsUntilTimestamp: response?.forEventsUntilTimestamp,
                    latestEventTimestamp: response?.latestEventTimestamp,
                    earliestEventTimestamp: response?.earliestEventTimestamp,
                    conversationStatuses: response?.conversationStatuses.filter(
                        (status) => status.conversationId !== activeConversationId
                    ),
                };

                nonActiveConversationStatuses.conversationStatuses.forEach((currentStatus) => {
                    const previousStatus = allE2EConversations.conversationStatuses.find(
                        (convo: ConversationStatus) => convo.conversationId === currentStatus.conversationId
                    );

                    if (!haveInitialisedConversations.includes(currentStatus.conversationId)) {
                        loadInitialMessagesForConversation(
                            currentStatus.conversationId,
                            currentStatus.latestMessageSeqId
                        );
                    } else if (previousStatus && previousStatus.latestMessageSeqId < currentStatus.latestMessageSeqId) {
                        dispatch(
                            populateConversationMessagesSinceLastFetch({
                                conversationId: currentStatus.conversationId,
                                seqId: previousStatus.latestMessageSeqId,
                            })
                        ).unwrap();
                    }
                });
            }

            setLastAllConvosSyncTimestamp(response.forEventsUntilTimestamp);
        } catch (error) {
            console.error(error);
        }
    };

    const monitorMessages = async () => {
        // TODO : remove when ready
        if (!isConnections) {
            return;
        }

        setSixtySecondTicker(sixtySecondTicker + 1);

        if (sixtySecondTicker === 3 || haveUnreadMessages) {
            setSixtySecondTicker(0);
            await checkForAllConversationMessages();
            await checkForNewActiveConversationMessages();
        } else {
            await checkForNewActiveConversationMessages();
        }
    };

    useIntervalEffect(monitorMessages, 15000);
};
